import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Sparkles, Zap, Loader2, ArrowLeft, Users, ShoppingCart, GraduationCap, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ===== Year-group detection =====
// Returns one of: 'seven-plus' | 'eleven-plus' | 'gcse' | 'a-levels' | 'unknown'
const detectStage = (user) => {
  if (!user) return 'unknown';
  const yg = (user.year_group || user.yearGroup || user.studentProfile?.yearGroup || '').toLowerCase();
  const ut = (user.user_type || user.userType || '').toLowerCase();
  // 7+ Entry: Year 1, Year 2
  if (/year ?[12]\b/.test(yg) || ut.includes('7+') || ut.includes('seven')) return 'seven-plus';
  // 11+ Entry: Year 5, Year 6
  if (/year ?[56]\b/.test(yg) || ut.includes('11+') || ut.includes('eleven')) return 'eleven-plus';
  // GCSE: Year 9, 10, 11
  if (/year ?(9|10|11)\b/.test(yg) || ut.includes('gcse') || ut.includes('ks4')) return 'gcse';
  // A-Levels: Year 12, 13
  if (/year ?1[23]\b/.test(yg) || ut.includes('a-level') || ut.includes('alevel')) return 'a-levels';
  return 'unknown';
};

// ===== NEW 2-Tier definitions (11+ Entry only: Foundation + Edpies Premium) =====
const TIERS = [
  {
    tier: 'foundation',
    name: 'Foundation',
    tagline: 'Perfect for learners building strong foundations and preparing confidently for key milestones.',
    icon: Sparkles,
    gradient: 'from-sky-400 to-blue-500',
    ring: 'ring-sky-200',
    features: [
      'Curated learning resources for your year group',
      'Practice papers across all core subjects',
      '4 mock papers per subject / year group',
      'Structured preparation for milestones',
      'Pay-As-You-Go mocks available',
    ],
    excludes: [
      'Growth Zone access',
      'AI-powered Challenger Series',
      'Advanced AI learning tools',
      'Unlimited mock papers',
    ],
    bestFor: 'Great for confident, self-directed practice.',
  },
  {
    tier: 'premium',
    name: 'Edpies Premium',
    tagline: 'Personalised, adaptive learning with unlimited practice.',
    icon: Zap,
    gradient: 'from-purple-500 to-fuchsia-600',
    ring: 'ring-purple-300',
    popular: true,
    features: [
      'Everything in Foundation',
      'Unlimited mock papers',
      'Full AI-powered learning tools',
      'Growth Zone for personalised tracking',
      'AI Challenger Series',
      'Smarter, adaptive learning',
      'Enhanced insights into strengths & weaknesses',
    ],
    bestFor: 'Students aiming to maximise confidence and academic progress.',
  },
];

const PRICING = {
  monthly: {
    foundation: { price: '5.99',  planId: 'foundation_monthly', suffix: '/month' },
    premium:    { price: '12.99', planId: 'premium_monthly',    suffix: '/month' },
  },
  annual: {
    foundation: { price: '60', planId: 'foundation_annual', suffix: '/year', saving: 'Save over 15%' },
    premium:    { price: '120', planId: 'premium_annual',   suffix: '/year', saving: 'Save over 20%' },
  },
};

// Single all-access plan for 7+, KS3, GCSE (Year 9-11)
const ALL_ACCESS_PRICING = {
  monthly: { price: '5.99', planId: 'all_access_monthly', suffix: '/month' },
  annual:  { price: '60',   planId: 'all_access_annual',  suffix: '/year', saving: 'Save over 15%' },
};

const FLEX_BUNDLES = [
  { planId: 'flex_mock_single',   label: 'Single Mock',   price: '2.99', perMock: '£2.99 / mock',                highlight: false },
  { planId: 'flex_mock_bundle_3', label: '3-Mock Bundle', price: '6.99', perMock: '~£2.33 / mock · save £2',     highlight: false },
  { planId: 'flex_mock_bundle_5', label: '5-Mock Bundle', price: '9.99', perMock: '~£2 / mock · best value',     highlight: true  },
];

const GCSE_PRICING = ALL_ACCESS_PRICING;  // kept name for back-compat in this file

const Subscription = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [billing, setBilling] = useState('monthly');

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const stage = useMemo(() => detectStage(user), [user]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPaymentStatus(sessionId);
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      setCheckingPayment(false);
      return;
    }
    setCheckingPayment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/checkout-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data;
      if (data.payment_status === 'paid' && data.subscription) {
        updateProgress && updateProgress({ subscription: data.subscription });
        const u = JSON.parse(localStorage.getItem('user'));
        u.subscription = data.subscription;
        localStorage.setItem('user', JSON.stringify(u));
        toast.success('Payment successful! Your subscription is now active.');
        setCheckingPayment(false);
        setTimeout(() => navigate('/categories'), 1500);
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        toast.error('Error verifying payment. Please contact support.');
        setCheckingPayment(false);
      }
    }
  };

  const handleSubscribe = async (planId) => {
    setLoading(planId);
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/subscription/create-checkout`,
        { planId, originUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;
      if (!data.requiresPayment) {
        updateProgress && updateProgress({ subscription: data.subscription });
        const u = JSON.parse(localStorage.getItem('user'));
        u.subscription = data.subscription;
        localStorage.setItem('user', JSON.stringify(u));
        toast.success('Free trial activated successfully!');
        navigate('/categories');
      } else {
        window.location.href = data.checkoutUrl;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Subscription failed. Please try again.');
    }
    setLoading(null);
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900">
        <Card className="max-w-md w-full mx-4 shadow-2xl" data-testid="payment-checking">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-16 w-16 text-purple-600 mx-auto mb-6 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment…</h2>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Stage labels for header
  const stageLabel = {
    'seven-plus':  '7+ Entry (Year 1–2)',
    'eleven-plus': '11+ Entry (Year 5–6)',
    'gcse':        'GCSE (Year 9–11)',
    'a-levels':    'A-Levels (Year 12–13)',
    'unknown':     'Choose Your Plan',
  }[stage];

  const showThreeTier = stage === 'eleven-plus' || stage === 'unknown';
  const showFlexBundles = stage === 'eleven-plus'; // 11+ Foundation only — PAYG mocks
  const showSinglePlan = stage === 'seven-plus' || stage === 'gcse';
  const showALevelsNotice = stage === 'a-levels';
  // Legacy alias used by JSX below — both 7+ and GCSE now use the same single all-access card.
  const showGcseCard = showSinglePlan;

  return (
    <div className="min-h-screen p-4 md:p-8 pb-24 md:pb-12 bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900" data-testid="subscription-page">
      <div className="max-w-7xl mx-auto space-y-10 animate-fade-in">
        {/* Header */}
        <div className="space-y-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <div className="text-center space-y-3">
            <Badge className="bg-white/10 text-white border border-white/20 hover:bg-white/10" data-testid="stage-badge">
              <GraduationCap className="h-3.5 w-3.5 mr-1.5" />
              {stageLabel}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              {showALevelsNotice ? 'A-Levels — Coming Soon' : 'Unlock Your Full Potential with Edpies'}
            </h1>
            {!showALevelsNotice && (
              <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto">
                {showSinglePlan
                  ? <>One simple plan — <span className="text-white font-semibold">£5.99/month or £60/year</span>, all features included.</>
                  : <>Start from just <span className="text-white font-semibold">£5.99/month</span> — upgrade anytime.</>}
              </p>
            )}
          </div>

          {/* Monthly / Annual toggle (hidden for A-Levels notice) */}
          {!showALevelsNotice && (
            <div className="flex justify-center pt-4">
              <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-1" data-testid="billing-toggle">
                <button
                  onClick={() => setBilling('monthly')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    billing === 'monthly' ? 'bg-white text-slate-900 shadow' : 'text-white/70 hover:text-white'
                  }`}
                  data-testid="billing-monthly-btn"
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBilling('annual')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    billing === 'annual' ? 'bg-white text-slate-900 shadow' : 'text-white/70 hover:text-white'
                  }`}
                  data-testid="billing-annual-btn"
                >
                  Annual
                  <Badge className="ml-2 bg-emerald-500 hover:bg-emerald-500 text-white text-[10px] px-1.5 py-0">
                    Save more
                  </Badge>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===== A-Levels: Coming Soon ===== */}
        {showALevelsNotice && (
          <Card className="bg-white/5 backdrop-blur border-white/10 max-w-2xl mx-auto" data-testid="a-levels-coming-soon">
            <CardContent className="p-8 md:p-10 text-center space-y-4">
              <div className="flex justify-center">
                <div className="bg-amber-500/20 p-4 rounded-2xl">
                  <Lock className="h-10 w-10 text-amber-300" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-white">A-Level subscription is launching later</h2>
              <p className="text-white/70 text-sm md:text-base">
                We're polishing the A-Level content library now. You can continue to explore the platform —
                we'll email you the moment subscriptions go live.
              </p>
              <Button
                onClick={() => navigate('/categories')}
                className="bg-white text-slate-900 hover:bg-white/90"
                data-testid="a-level-explore-btn"
              >
                Continue exploring
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ===== 11+ : 2-Tier Cards (Foundation + Edpies Premium) ===== */}
        {showThreeTier && (
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {TIERS.map((t) => {
              const p = PRICING[billing][t.tier];
              const Icon = t.icon;
              const isPopular = t.popular;
              return (
                <Card
                  key={t.tier}
                  className={`relative overflow-hidden shadow-2xl transition-all duration-300 hover:-translate-y-2 ${
                    isPopular ? `ring-4 ${t.ring} scale-[1.02]` : 'ring-1 ring-white/10'
                  }`}
                  data-testid={`plan-card-${t.tier}`}
                >
                  {isPopular && (
                    <div className="absolute top-0 right-0 z-10">
                      <Badge className="bg-gradient-to-r from-purple-500 to-fuchsia-600 text-white font-bold px-4 py-1.5 rounded-none rounded-bl-xl text-xs shadow-lg">
                        MOST POPULAR
                      </Badge>
                    </div>
                  )}
                  {billing === 'annual' && p.saving && !isPopular && (
                    <div className="absolute top-0 right-0 z-10">
                      <Badge className="bg-emerald-500 text-white font-semibold px-3 py-1 rounded-none rounded-bl-xl text-xs">
                        {p.saving}
                      </Badge>
                    </div>
                  )}

                  <CardHeader className={`bg-gradient-to-br ${t.gradient} text-white text-center py-8`}>
                    <div className="flex justify-center mb-4">
                      <div className="bg-white/20 backdrop-blur p-3 rounded-2xl">
                        <Icon className="h-10 w-10" />
                      </div>
                    </div>
                    <CardTitle className="text-3xl font-bold">{t.name}</CardTitle>
                    <CardDescription className="text-white/90 mt-1 text-sm">{t.tagline}</CardDescription>
                    <div className="pt-4">
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-5xl font-bold">£{p.price}</span>
                        <span className="text-white/80 text-base">{p.suffix}</span>
                      </div>
                      {billing === 'annual' && isPopular && p.saving && (
                        <p className="text-white/90 text-xs mt-2 font-medium">{p.saving}</p>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="p-6 space-y-5 bg-white">
                    <ul className="space-y-3">
                      {t.features.map((f, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">{f}</span>
                        </li>
                      ))}
                      {showFlexBundles && t.tier === 'foundation' && (
                        <li className="flex items-start gap-3 pt-2 border-t border-dashed border-gray-200">
                          <ShoppingCart className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-sm">
                            <span className="font-semibold">Plus:</span> buy extra mocks (Pay-As-You-Go) — see below
                          </span>
                        </li>
                      )}
                    </ul>

                    {t.excludes && (
                      <div className="pt-3 border-t border-gray-100">
                        <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Not included</p>
                        <ul className="space-y-2">
                          {t.excludes.map((f, idx) => (
                            <li key={idx} className="flex items-start gap-2.5 text-sm">
                              <span className="h-4 w-4 text-rose-500 flex-shrink-0 mt-0.5 font-bold">✕</span>
                              <span className="text-gray-500">{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 italic">
                        Best for: <span className="text-gray-700 not-italic">{t.bestFor}</span>
                      </p>
                    </div>

                    <Button
                      onClick={() => handleSubscribe(p.planId)}
                      disabled={loading !== null}
                      className={`w-full h-12 text-base font-semibold bg-gradient-to-r ${t.gradient} text-white hover:opacity-95`}
                      data-testid={`subscribe-${t.tier}-btn`}
                    >
                      {loading === p.planId ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Processing…
                        </>
                      ) : (
                        `Choose ${t.name}`
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* ===== 11+ : Flex Pay-As-You-Go Mock Bundles ===== */}
        {showFlexBundles && (
          <Card className="bg-white/5 backdrop-blur border-white/10" data-testid="flex-bundles-card">
            <CardContent className="p-6 md:p-8 space-y-6">
              <div className="flex items-start gap-3">
                <div className="bg-sky-500/20 p-2 rounded-xl">
                  <ShoppingCart className="h-5 w-5 text-sky-300" />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-semibold text-white">11+ Mock Test Top-ups (Pay-As-You-Go)</h3>
                  <p className="text-white/70 text-sm mt-1">
                    For 11+ <span className="font-semibold text-white">Foundation</span> tier — buy extra mocks anytime, no subscription change.
                    <span className="text-white/50"> (Premium already includes unlimited mocks.)</span>
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {FLEX_BUNDLES.map((b) => (
                  <div
                    key={b.planId}
                    className={`rounded-xl p-5 border transition-all hover:-translate-y-1 ${
                      b.highlight
                        ? 'bg-gradient-to-br from-emerald-500/20 to-sky-500/20 border-emerald-400/50'
                        : 'bg-white/5 border-white/10'
                    }`}
                    data-testid={`flex-bundle-${b.planId}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-white font-semibold">{b.label}</span>
                      {b.highlight && (
                        <Badge className="bg-emerald-500 text-white text-[10px]">BEST VALUE</Badge>
                      )}
                    </div>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-3xl font-bold text-white">£{b.price}</span>
                    </div>
                    <p className="text-white/60 text-xs mb-4">{b.perMock}</p>
                    <Button
                      onClick={() => handleSubscribe(b.planId)}
                      disabled={loading !== null}
                      variant="outline"
                      className="w-full bg-white/10 hover:bg-white/20 text-white border-white/20"
                      data-testid={`buy-${b.planId}-btn`}
                    >
                      {loading === b.planId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Buy Now'
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== Single All-Access Plan (7+ Entry + GCSE) ===== */}
        {showSinglePlan && (
          <div className="max-w-xl mx-auto">
            <Card
              className="relative overflow-hidden shadow-2xl ring-4 ring-purple-300"
              data-testid="plan-card-all-access"
            >
              <div className="absolute top-0 right-0 z-10">
                <Badge className="bg-gradient-to-r from-purple-500 to-fuchsia-600 text-white font-bold px-4 py-1.5 rounded-none rounded-bl-xl text-xs shadow-lg">
                  ALL FEATURES
                </Badge>
              </div>

              <CardHeader className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-center py-10">
                <div className="flex justify-center mb-4">
                  <div className="bg-white/20 backdrop-blur p-3 rounded-2xl">
                    <GraduationCap className="h-10 w-10" />
                  </div>
                </div>
                <CardTitle className="text-3xl font-bold">Edpies All-Access</CardTitle>
                <CardDescription className="text-white/90 mt-1 text-sm">
                  One simple plan — everything your child needs, included.
                </CardDescription>
                <div className="pt-4">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold">£{ALL_ACCESS_PRICING[billing].price}</span>
                    <span className="text-white/80 text-base">{ALL_ACCESS_PRICING[billing].suffix}</span>
                  </div>
                  {billing === 'annual' && ALL_ACCESS_PRICING.annual.saving && (
                    <p className="text-white/90 text-xs mt-2 font-medium">{ALL_ACCESS_PRICING.annual.saving}</p>
                  )}
                </div>
              </CardHeader>

              <CardContent className="p-6 space-y-5 bg-white">
                <ul className="space-y-3">
                  {[
                    'Unlimited access to all year-group content',
                    'Resource Hub + AI Learning Hub',
                    'Adaptive Learning + Growth Zone',
                    'Unlimited Practice + Mock Papers',
                    'AI Challenger Series',
                    'Performance + Parent Dashboards',
                  ].map((f, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(ALL_ACCESS_PRICING[billing].planId)}
                  disabled={loading !== null}
                  className="w-full h-12 text-base font-semibold bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:opacity-95"
                  data-testid="subscribe-all-access-btn"
                >
                  {loading === ALL_ACCESS_PRICING[billing].planId ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Processing…
                    </>
                  ) : (
                    `Get All-Access`
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== Free trial banner (hidden for A-Levels) ===== */}
        {!showALevelsNotice && (
          <Card className="bg-white/5 backdrop-blur border-white/10" data-testid="free-trial-banner">
            <CardContent className="p-6 md:p-8 text-center space-y-3">
              <div className="flex items-center justify-center gap-2 text-white">
                <Sparkles className="h-5 w-5 text-amber-300" />
                <span className="text-lg font-semibold">5-Day Free Trial</span>
              </div>
              <p className="text-white/70 text-sm md:text-base">
                Try Edpies risk-free — 5 practice tests, 5 mock papers &amp; Ed's English Library included.
              </p>
              <Button
                variant="outline"
                onClick={() => handleSubscribe('free')}
                disabled={loading !== null}
                className="bg-white text-slate-900 hover:bg-white/90 border-0"
                data-testid="start-free-trial-btn"
              >
                {loading === 'free' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Start Free Trial
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Skip */}
        <div className="text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/categories')}
            className="text-white/70 hover:bg-white/10 hover:text-white"
            data-testid="skip-btn"
          >
            Skip for now and explore
          </Button>
        </div>

        {/* Secure payment */}
        <div className="text-center text-white/50 text-xs">
          <p>Secure payment powered by Stripe</p>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
